/**
 * usePressureField.ts — يجلب شبكة الضغط (هكتوباسكال) للإطار الحالي ويحتفظ بها.
 *
 * لا كنتور هنا ولا هندسة: الخطوط تُرسم في شيدر طبقة الضغط. هذه الشبكة تخدم النصوص
 * وحدها — حرفا H/L وقيمهما، وتسميات القيم على الخطوط — وهي المعلومة الوحيدة التي
 * لا يستطيع الـ GPU إخراجها إلى JavaScript.
 */
import { useEffect, useState } from 'react';
import { rasterUrl, whenRasterVersionKnown } from '../../../services/rasterVersion';
import { VALUE_RANGES } from '../webgl/weatherTextures';
import type { PressureFieldRequest, PressureFieldResponse } from '../workers/pressureFieldWorker';

export interface PressureField {
    values: Float32Array;
    width: number;
    height: number;
    /** الدقّة كما كُشفت من البكسل — تحكم عرض نواة التمويه (‏blurStepScale). */
    p16: boolean;
}

const CACHE_MAX = 12;
const cache = new Map<string, PressureField>();

let worker: Worker | null = null;
const pending = new Map<string, ((f: PressureField | null) => void)[]>();

function getWorker(): Worker {
    if (!worker) {
        worker = new Worker(new URL('../workers/pressureFieldWorker.ts', import.meta.url), { type: 'module' });
        worker.onmessage = (ev: MessageEvent<PressureFieldResponse>) => {
            const { key, values, width, height, p16 } = ev.data;
            const waiting = pending.get(key);
            pending.delete(key);
            const field = values ? { values, width, height, p16 } : null;
            if (field) {
                cache.set(key, field);
                if (cache.size > CACHE_MAX) {
                    const oldest = cache.keys().next().value;
                    if (oldest !== undefined) cache.delete(oldest);
                }
            }
            waiting?.forEach((resolve) => resolve(field));
        };
    }
    return worker;
}

function load(key: string, req: PressureFieldRequest): Promise<PressureField | null> {
    const hit = cache.get(key);
    if (hit) return Promise.resolve(hit);
    return new Promise((resolve) => {
        const waiting = pending.get(key);
        if (waiting) { waiting.push(resolve); return; }
        pending.set(key, [resolve]);
        getWorker().postMessage(req);
    });
}

const file = (p16: boolean, index: number) =>
    `${p16 ? 'pressure16' : 'pressure'}_${String(index).padStart(3, '0')}.png`;

/**
 * @param p16 هل يوفّر النموذج نظير 16-بت؟ الرمادي رافدٌ لا بديل: مستوياً 0.373
 *            هكتوباسكال لكل خطوة، وهو خشنٌ للخطوط لكنه يكفي لرقمٍ مُقرَّب على حرف.
 */
export function usePressureField(
    timeIndex: number, dir: string, enabled: boolean, p16: boolean, frameCount = 0,
): PressureField | null {
    const [field, setField] = useState<PressureField | null>(null);

    useEffect(() => {
        if (!enabled) { setField(null); return; }
        const key = `${dir}${p16 ? '16' : ''}|${timeIndex}`;
        /*
         * وتُطفأ الحروف **لحظة تغيّر الساعة**، لا عند فشل الجلب وحده: الشبكة تصل بعد
         * الطلب بلحظات، وخلالها كانت حروف الساعة السابقة معلّقة فوق خريطةٍ خطوطُها
         * اختفت بالفعل. والكاش يُقرأ هنا مباشرةً فالساعات المحمّاة لا تومض أصلاً —
         * وهو ما يفسّر أن العطب كان يظهر خارج الجزء الأزرق من الشريط وحده.
         */
        setField(cache.get(key) ?? null);
        let cancelled = false;
        const [vmin, vmax] = VALUE_RANGES.pressure;
        const request = (index: number) => ({
            key: `${dir}${p16 ? '16' : ''}|${index}`,
            url: rasterUrl(dir, file(p16, index)),
            urlAlt: p16 ? rasterUrl(dir, file(false, index)) : undefined,
            vmin, vmax,
        });
        void whenRasterVersionKnown().then(() => {
            if (cancelled) return;
            void load(key, request(timeIndex)).then((f) => {
                /*
                 * ── الحروف تتبع الخطوط: إمّا معاً أو لا شيء ────────────────────
                 *
                 * كان الفشل يُبقي **آخر شبكة معروفة** كي لا تختفي الحروف. لكن الخطوط
                 * تُرسم من النسيج مباشرةً وتختفي فور غيابه، فينكسر الاقتران: أرقامٌ
                 * من ساعةٍ سابقة فوق خريطةٍ بلا خطوط. رصده المستخدم في 2026-09-20 —
                 * على الجزء المحمّى من الشريط يظهر الاثنان، وخارجه تبقى الأرقام وحدها.
                 *
                 * والوعد المكتوب عند تركيب الطبقة كان عكس ذلك: «تُشتقّ من نفس الشبكة
                 * ونفس المستويات، فيستحيل بناءً أن يظهر حرف بلا حلقة». فنُنفّذه:
                 * شبكةٌ لهذه الساعة أو لا حروف. ورقمٌ صحيح من ساعةٍ أخرى أسوأ من لا
                 * رقم، لأنه لا يُعلن أنه من ساعةٍ أخرى.
                 */
                if (!cancelled) setField(f);

                /* ── استباق الساعة التالية ────────────────────────────────────────
                   صورة الضغط قد تكون مخزّنة سلفاً (تُحمَّى مع بقيّة مدخلات الإطار)،
                   لكنّ الأرقام لا تُشتقّ من الصورة بل من شبكةٍ تُفكّ وتُموَّه في عامل.
                   ذلك العمل لم يكن يبدأ إلّا بعد بلوغ الساعة، فتصل الأرقام متأخّرةً عن
                   خطوطها وألوانها — تأخّرٌ يُرى أثناء التشغيل تحديداً.

                   إطارٌ واحد يكفي: الشريط يتقدّم ساعةً ساعة، فالتالي هو كل ما سيُطلب
                   بعد لحظات. وليست كلفةً إضافية بل نفس الفكّ مقدَّماً — الحصيلة على
                   مدى التشغيل واحدة، وموضعها وحده تغيّر. النتيجة محفوظة في الكاش
                   نفسه (12 شبكة) فيجدها الطلب التالي جاهزة. */
                const next = timeIndex + 1;
                if (!cancelled && frameCount > 0 && next < frameCount) {
                    void load(`${dir}${p16 ? '16' : ''}|${next}`, request(next)).catch(() => undefined);
                }
            });
        });
        return () => { cancelled = true; };
    }, [timeIndex, dir, enabled, p16, frameCount]);

    return field;
}
